import { useCallback, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { FaHeart } from "react-icons/fa";
import { GetSong } from "../../../api";
import * as Module from "../../../modules";
import MusicIcon from '../../../images/icon.png'
import "./style.css";
import { stateProps } from "../../../modules/state";
import { db } from '../../../firebaseClient';
import { collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { MOBILE_MAX_WIDTH, BREAKPOINTS } from "../styles/const_style";

const SongsBox = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
    padding: 16px 12px 92px;
    box-sizing: border-box;
    gap: 16px;

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        padding: 12px 8px 80px;
        gap: 12px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        padding: 8px 6px 72px;
        gap: 10px;
    }
`;

const Text = styled.p`
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    &.selected {
        /* text-decoration: underline; */

        &::before {
            content: '>';
            margin-right: 4px;
            text-decoration: none;
        }

        &::after {
            content: '<';
            margin-left: 4px;
            text-decoration: none;
        }
    }
`;

const SongBox = styled.div`
    /*cursor: pointer;*/
    width: 100%;
    min-width: 80px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: 80px;

    border: 2px solid var(--text-color);
    box-sizing: border-box;
    /* border-radius: 12px; */
    padding: 10px 12px;

    font-weight: 400;
    font-size: 0.75rem;

    color: var(--text-color);
    background: var(--bg-color);
    border-radius: 12px;
    box-shadow: 0px 1px var(--text-color);

    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
        transform: translateY(0);
    }

    & > div:nth-child(1) {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;

        & img {
            margin-right: 16px;

            max-width: 56px;
            max-height: 56px;
        }
    }

    & > div:nth-child(2) {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;

        & p {
            margin: 0;
        }

        & > div {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
        }
    }

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        height: 72px;
        padding: 8px 10px;
        font-size: 0.7rem;
        border-radius: 10px;

        & > div:nth-child(1) {
            & img {
                margin-right: 12px;
                max-width: 48px;
                max-height: 48px;
            }
        }
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        height: 66px;
        padding: 6px 8px;
        font-size: 0.65rem;
        border-radius: 8px;

        & > div:nth-child(1) {
            & img {
                margin-right: 8px;
                max-width: 42px;
                max-height: 42px;
            }
        }
    }
`;

const HeartIcon = styled(FaHeart)`
    cursor: pointer;
    transition: all 100ms ease-in;
    &:hover {
        color: #ffabab;
        transform: scale(1.1);
    }
`;

const LoadingIndicator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    color: var(--text-color);
    font-size: 0.8rem;
`;

const LoadMoreTrigger = styled.div`
    width: 100%;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color);
    opacity: 0.7;

    & > p {
        margin: 8px 0;
        font-size: 0.9rem;
    }

    & > span {
        font-size: 0.75rem;
        opacity: 0.6;
    }
`;

const EndOfList = styled.div`
    padding: 16px;
    text-align: center;
    font-size: 0.75rem;
    color: var(--text-color);
    opacity: 0.5;
`;

interface SongProps {
    title: string,
    artist: string,
    folder: string,
    id: string,
    onClick: (title: string, artist: string, folder: string, id: string) => void,
    onDetailClick: (e: React.MouseEvent<HTMLDivElement>, type: string, query: string) => void,
    isFavorite: boolean,
    onFavoriteToggle: (songId: string, isFavorite: boolean) => void;
}

const Song = ({title, artist, folder, id, onClick, onDetailClick, isFavorite, onFavoriteToggle }: SongProps) => {
    const { _id } = useSelector((state: Module.RootState) => ({
        _id: state.song.id,
    }), shallowEqual);

    const getTitle = useCallback((input: string) => {
        return input.replace(/\(.*?\)/g, "").trim();
    }, []);
    const getIndex = useCallback((input: string) => {
        const match = input.match(/(\(\d\))/);
        if(match) return match[1];
        else return '';
    }, []);

    const parseTitle = useCallback((input: string) => {
        const idx = getIndex(input);
        const title = getTitle(input);
        if(idx !== '') return `${title} ${idx}`;
        else return title;
    }, [getTitle, getIndex]);

    const isCurrentPlaying = (id === _id);
    return (
        <SongBox onClick={() => onClick(title, artist, folder, id)}>
            <div>
                <img src={MusicIcon} alt="Music Icon"/>
            </div>
            <div>
                <div>
                    <Text onClick={(e: React.MouseEvent<HTMLDivElement>) => onDetailClick(e, 'title', title)} className={`songname ${isCurrentPlaying && 'selected'}`}>
                        {parseTitle(title)}
                    </Text>
                    <HeartIcon
                        size='0.9rem'
                        color={isFavorite ? '#ff4444' : 'grey'}
                        onClick={(e) => {
                            e.stopPropagation();
                            onFavoriteToggle(id, isFavorite);
                        }}
                    />
                </div>
                <div>
                    <Text onClick={(e: React.MouseEvent<HTMLDivElement>) => onDetailClick(e, 'artist', artist)} className="artist">{artist}</Text>
                    <Text onClick={(e: React.MouseEvent<HTMLDivElement>) => onDetailClick(e, 'folder', folder)} className="folder">{folder}</Text>
                </div>
            </div>
        </SongBox>
    );
}

interface SongsProps {
    onSearch: (type: string, query: string, page?: number, viewCount?: number) => void;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    showFavoritesOnly?: boolean;
    onSongSelect?: () => void;
}

const Songs = ({ onSearch, onLoadMore, hasMore = false, isLoadingMore = false, showFavoritesOnly = false, onSongSelect }: SongsProps) => {
    const dispatch = useDispatch();
    const { songs, initial } = useSelector((state: stateProps) => ({
        songs: state.data.songs,
        initial: state.data.initial
    }), shallowEqual);

    const [favorites, setFavorites] = useState<string[]>([]);
    const [anonymousUserId, setAnonymousUserId] = useState<string>('');
    const loadMoreRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let userId = localStorage.getItem('anonymous_user_id');
        if (!userId) {
            userId = uuidv4();
            localStorage.setItem('anonymous_user_id', userId);
        }
        setAnonymousUserId(userId);
    }, []);

    const fetchFavorites = useCallback(async (userId: string) => {
        if (!userId) return;
        try {
            const snap = await getDocs(collection(db, 'users', userId, 'favorites'));
            setFavorites(snap.docs.map(d => d.id));
        } catch (error) {
            console.error('Error fetching favorites:', error);
        }
    }, []);

    useEffect(() => {
        if (anonymousUserId) {
            fetchFavorites(anonymousUserId);
        }
    }, [anonymousUserId, fetchFavorites]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!onLoadMore || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoadingMore) {
                    onLoadMore();
                }
            },
            {
                threshold: 0.1,
                rootMargin: '100px',
            }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [onLoadMore, hasMore, isLoadingMore]);

    const handleFavoriteToggle = async (songId: string, isFavorite: boolean) => {
        if (!anonymousUserId) return;
        const favRef = doc(db, 'users', anonymousUserId, 'favorites', songId);
        try {
            if (isFavorite) {
                await deleteDoc(favRef);
                setFavorites(favorites.filter(id => id !== songId));
            } else {
                await setDoc(favRef, { songId, addedAt: serverTimestamp() });
                setFavorites([...favorites, songId]);
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };


    const onDetailClick = (e: React.MouseEvent<HTMLDivElement>, _type: string, _query: string) => {
        e.stopPropagation();
        onSearch(_type, _query, 1);
    };

    const onClick = useCallback((title: string, artist: string, folder: string, id: string) => {
        if(!id) {
            console.error('id is undefined');
            return;
        }
        dispatch(Module.setSongInfo({imgSrc: '', title: title, artist: artist, folder: folder}));
        dispatch(Module.startLoading());

        // Collapse bottom sheet when song is selected
        onSongSelect?.();

        GetSong(`/api/goodrecord/stream?id=${id}`, (response, error) => {
            if(error) {
                console.error(error);
                dispatch(Module.endLoading());
                return;
            }
            if(!response) {
                console.error('response is null');
                dispatch(Module.endLoading());
                return;
            }
            const audioUrl = URL.createObjectURL(response);
            dispatch(Module.setPlayId(id));
            dispatch(Module.setSrc(audioUrl));
            dispatch(Module.endLoading());
            dispatch(Module.displayPlayer());
        });
    }, [dispatch, onSongSelect]);

    // Filter songs based on favorites if needed
    const displayedSongs = showFavoritesOnly
        ? songs.filter((song: { id: string }) => favorites.includes(song.id))
        : songs;

    return (
        <SongsBox>
            {displayedSongs.length > 0 && !initial ? (
                <>
                    {displayedSongs.map((song: { id: string; title: string; artist: string; folder: string }, idx: number) => (
                        <Song
                            onDetailClick={onDetailClick}
                            onClick={onClick}
                            key={`song_${song.id || idx}_${idx}`}
                            id={song.id}
                            title={song.title}
                            artist={song.artist}
                            folder={song.folder}
                            isFavorite={favorites.includes(song.id)}
                            onFavoriteToggle={handleFavoriteToggle}
                        />
                    ))}

                    {/* Infinite scroll trigger */}
                    {hasMore && (
                        <LoadMoreTrigger ref={loadMoreRef}>
                            {isLoadingMore && (
                                <LoadingIndicator>
                                    로딩 중...
                                </LoadingIndicator>
                            )}
                        </LoadMoreTrigger>
                    )}

                    {/* End of list indicator */}
                    {!hasMore && displayedSongs.length > 0 && (
                        <EndOfList>
                            모든 곡을 불러왔습니다
                        </EndOfList>
                    )}
                </>
            ) : !initial ? (
                <EmptyState>
                    <p>해당 데이터는 존재하지 않습니다</p>
                    <span>검색어를 변경하거나 필터를 해제해 보세요</span>
                </EmptyState>
            ) : (
                <EmptyState>
                    <p>검색어를 입력하세요</p>
                    <span>제목, 아티스트, 또는 폴더로 검색할 수 있습니다</span>
                </EmptyState>
            )}
        </SongsBox>
    );
};

export default Songs;
